import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  retainSearchParams,
  useRouterState,
} from '@tanstack/react-router';

import { Disclaimer } from '@/components/compliance/Disclaimer';
import { BottomTabBar } from '@/components/layout/BottomTabBar';
import { SideNav } from '@/components/layout/SideNav';
import { TopNav } from '@/components/layout/TopNav';
import { ChatWidget } from '@/features/assistant';
import { SiteFlash } from '@/features/auth/SiteFlash';
import { parseAsOf } from '@/lib/asof/asof';
import { isNativeApp } from '@/lib/platform';
import { appQueryPersister, buildPersistOptions } from '@/lib/query-persist';

/**
 * =============================================================================
 *  §10.1：`__root.tsx` ＝ TopNav（含 as_of 時光機）+ Sidebar + Outlet + Disclaimer。
 *  lg 以下 Sidebar 換成底部分頁列（BottomTabBar），內容底部讓出它的高度（`pb-tabbar`）。
 *
 *  兩件在這裡定案、之後再也不必重做的事：
 *
 *  1. **`as_of` 是 root 的 search param。** 宣告在根，所有子路由都繼承得到，
 *     於是「每一個資料 endpoint 都接受 ?as_of」在前端側的對應物
 *     ——「每一個頁面都看得到 as_of」——不需要每個路由各自宣告一次。
 *
 *  2. **`retainSearchParams(['as_of'])`。** 換頁時 as_of 不會掉。
 *     少了這一行，使用者從 explorer 點到 status 再點回來就悄悄回到了今天，
 *     而他不會發現——那是時光機最難查的一種壞法。
 * ========================================================================== */

export interface RouterContext {
  queryClient: QueryClient;
}

export interface RootSearch {
  as_of?: string;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: (search: Record<string, unknown>): RootSearch => {
    const as_of = parseAsOf(search['as_of']);
    // 不合法的 as_of 一律丟掉而不是報錯：一個被截斷的分享連結應該退化成
    // 即時視圖，不該變成一個錯誤頁。
    return as_of === undefined ? {} : { as_of };
  },
  search: { middlewares: [retainSearchParams(['as_of'])] },
  component: RootLayout,
});

/**
 * 模組層級取一次。每次 render 都新建一個 persister 會讓 IndexedDB 連線一直重開；
 * 也必須與登出時 `clearUserCache` 刪的是**同一個**（見 lib/query-persist.ts）。
 */
const persister = appQueryPersister();

// iOS 殼：在第一次 render 前標上 data-native，globals.css 據此關掉頁面回彈與點擊灰底。
// 模組層級做一次就夠——是不是 native 在這個 WebView 的一生裡不會改變。
if (isNativeApp()) document.documentElement.dataset['native'] = 'ios';

function RootLayout() {
  const { queryClient } = Route.useRouteContext();
  const isLoading = useRouterState({ select: (s) => s.status === 'pending' });

  const shell = (
    <div className="flex min-h-dvh flex-col bg-surface-0 text-fg-1 max-lg:pb-tabbar">
      <TopNav />
      <div className="flex flex-1 flex-col lg:flex-row min-h-0">
        <SideNav />
        {/* 頁尾放在內容欄裡而不是整列之後：側邊欄是 sticky，它的容器（這一列）必須一路延伸到頁面底，
            捲到最底時側邊欄才不會被頁尾往上推。
            橫拿 iPhone 時瀏海在左或右：內容欄讓開 safe area（lg 以上左側由 SideNav 讓）。 */}
        <div className="flex min-w-0 flex-1 flex-col pr-[var(--safe-right)] max-lg:pl-[var(--safe-left)]">
          <main
            className="min-w-0 flex-1 px-3 py-3 sm:px-5 sm:py-5 lg:px-8 lg:py-6"
            aria-busy={isLoading}
            data-testid="app-main"
          >
            <SiteFlash />
            <Outlet />
          </main>
          <footer className="flex flex-col gap-2 border-t border-border-1/60 bg-surface-1/90 px-4 py-4 sm:px-6">
            <Disclaimer />
            <nav aria-label="法律資訊" className="flex flex-wrap gap-x-3 gap-y-1 text-11 text-muted">
              <Link to="/privacy" className="hover:text-fg-1 hover:underline">
                隱私權政策
              </Link>
              <span aria-hidden="true">／</span>
              <Link to="/terms" className="hover:text-fg-1 hover:underline">
                服務條款
              </Link>
            </nav>
          </footer>
        </div>
      </div>
      <ChatWidget />
      <BottomTabBar />
    </div>
  );

  if (persister === null) {
    return <QueryClientProvider client={queryClient}>{shell}</QueryClientProvider>;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={buildPersistOptions(persister)}
    >
      {shell}
    </PersistQueryClientProvider>
  );
}
